import { supabase } from "@/integrations/supabase/client";

/** Safe DOM id derived from an article href. Kept in sync with portfolio.tsx. */
export function articleAnchorId(href: string): string {
  return `article-${href.replace(/[^a-zA-Z0-9]/g, "-").slice(0, 100)}`;
}

export type SearchCategory = "Pages" | "Articles" | "Podcasts" | "Dataviz" | "Projets";

export type SearchEntry = {
  id: string;
  category: SearchCategory;
  title: string;
  subtitle?: string;
  keywords?: string;
  to: string;
  hash?: string;
};

/** Pages statiques du site, toujours disponibles (même hors ligne / avant le fetch). */
export const STATIC_PAGES: ReadonlyArray<SearchEntry> = [
  {
    id: "page-/",
    category: "Pages",
    title: "Accueil",
    to: "/",
    keywords: "home mathys gautier datajournaliste",
  },
  {
    id: "page-/portfolio",
    category: "Pages",
    title: "Portfolio",
    to: "/portfolio",
    keywords: "articles presse podcasts publications",
  },
  {
    id: "page-/dataviz",
    category: "Pages",
    title: "Dataviz",
    to: "/dataviz",
    keywords: "visualisation données graphiques datawrapper",
  },

  {
    id: "page-/parcours",
    category: "Pages",
    title: "Parcours",
    to: "/parcours",
    keywords: "cv expériences formation compétences",
  },
  {
    id: "page-/contact",
    category: "Pages",
    title: "Contact",
    to: "/contact",
    keywords: "email téléphone réseaux",
  },
];

/** Charge l'index de recherche (contenu publié) depuis le backend. */
export async function fetchSearchIndex(): Promise<SearchEntry[]> {
  const [articles, podcasts, dataviz, projets] = await Promise.all([
    supabase
      .from("articles")
      .select("id,title,outlet,section,excerpt,href")
      .eq("is_published", true),
    supabase
      .from("podcast_episodes")
      .select("id,title,outlet,episode_label,excerpt")
      .eq("is_published", true),
    supabase
      .from("dataviz_projects")
      .select("id,title,description,tools,publication_type")
      .eq("is_published", true),
    supabase.from("projets").select("id,title,description,theme").eq("is_published", true),
  ]);

  const entries: SearchEntry[] = [];

  for (const a of articles.data ?? []) {
    if (!a.title) continue;
    entries.push({
      id: `article-${a.id}`,
      category: "Articles",
      title: a.title,
      subtitle: [a.outlet, a.section].filter(Boolean).join(" · "),
      keywords: a.excerpt ?? "",
      to: "/portfolio",
      hash: a.href ? articleAnchorId(a.href) : undefined,
    });
  }

  for (const p of podcasts.data ?? []) {
    if (!p.title) continue;
    entries.push({
      id: `podcast-${p.id}`,
      category: "Podcasts",
      title: p.title,
      subtitle: [p.outlet, p.episode_label].filter(Boolean).join(" · "),
      keywords: p.excerpt ?? "",
      to: "/portfolio",
    });
  }

  for (const d of dataviz.data ?? []) {
    if (!d.title) continue;
    entries.push({
      id: `dataviz-${d.id}`,
      category: "Dataviz",
      title: d.title,
      subtitle: d.publication_type ?? undefined,
      keywords: [d.description ?? "", (d.tools ?? []).join(" ")].join(" "),
      to: "/dataviz",
    });
  }

  for (const p of projets.data ?? []) {
    if (!p.title) continue;
    entries.push({
      id: `projet-${p.id}`,
      category: "Projets",
      title: p.title,
      subtitle: p.theme ?? undefined,
      keywords: p.description ?? "",
      to: "/projets",
    });
  }

  return entries;
}
